import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useScopeParams } from "@/hooks/useScopeParams";
import Loader from "@/components/UI/Loader";
import { loadMarkdown, getManifest } from "@/utils/markdownLoader";
import {
  formatManifestDate,
  semverCompareDesc,
  type ReleaseNoteListEntry,
} from "./releaseNoteMarkdown";
import { scrollToWikiTop } from "../utils/scrollToWikiTop";

/**
 * All Release Notes
 *
 * Renders the markdown release-note index as a chronological changelog list.
 */

function AllReleaseNotesPage() {
  const { buildUrl } = useScopeParams();
  const [entries, setEntries] = useState<ReleaseNoteListEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    scrollToWikiTop();

    let mounted = true;

    const load = async () => {
      try {
        const manifest = await getManifest("release-notes");
        const files = (manifest.files ?? []).filter(
          (file: { slug: string }) => file.slug !== "all-release-notes",
        );

        const loaded = await Promise.all(
          files.map(async (file: { slug: string; date?: string }) => {
            const data = await loadMarkdown(`release-notes/${file.slug}`);
            return {
              slug: file.slug,
              version: String(
                data?.frontmatter?.releaseVersion ?? file.slug.replace("v", ""),
              ),
              title: String(data?.frontmatter?.title ?? `Vivo ${file.slug}`),
              description: String(data?.frontmatter?.description ?? ""),
              displayDate: formatManifestDate(file.date ?? "01/01/2025"),
              lastUpdated: String(data?.frontmatter?.lastUpdated ?? ""),
              year: parseInt((file.date ?? "01/01/2025").split("/")[2], 10),
            } satisfies ReleaseNoteListEntry;
          }),
        );

        loaded.sort((a, b) => semverCompareDesc(a.version, b.version));
        if (mounted) setEntries(loaded);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const byYear = entries.reduce<Record<number, ReleaseNoteListEntry[]>>(
    (acc, entry) => {
      if (!acc[entry.year]) acc[entry.year] = [];
      acc[entry.year].push(entry);
      return acc;
    },
    {},
  );
  const years = Object.keys(byYear)
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div className="wiki-markdown mx-auto max-w-[820px] overflow-visible bg-transparent px-9 pb-24 pt-10">
      <div className="mb-12">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0c98d0]">
          Vivo · Release Notes
        </p>
        <h1
          className="m-0 text-[40px] font-medium leading-[1.1] tracking-[-0.025em] text-[#17313a]"
          data-testid="wiki-page-title"
        >
          Release Notes
        </h1>
        {!loading && (
          <p className="mt-2.5 text-[15px] text-[#6b8390]">
            {entries.length} releases — Vivo v
            {entries[entries.length - 1]?.version} through v
            {entries[0]?.version}
          </p>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader type="blue" />
        </div>
      )}

      {!loading &&
        years.map((year) => (
          <div key={year} className="mb-12">
            <div className="mb-2 flex items-center gap-3">
              <span className="shrink-0 text-[12px] font-bold tracking-[0.04em] text-[#8fa8b2]">
                {year}
              </span>
              <div className="h-px flex-1 bg-[#e7eaec]" />
            </div>

            {byYear[year].map((entry) => (
              <Link
                key={entry.slug}
                to={buildUrl(`wiki/release-notes/${entry.slug}`)}
                className="group block no-underline"
              >
                <div className="grid cursor-pointer grid-cols-[auto_auto_1fr_auto] items-center gap-x-5 rounded-[10px] px-4 py-3.5 transition-colors group-hover:bg-[#f0f9fd] group-focus-visible:bg-[#f0f9fd]">
                  <span className="inline-flex min-w-[52px] shrink-0 items-center justify-center rounded-full bg-[#e8f5fb] px-2.5 py-0.5 text-[12px] font-bold tracking-[0.02em] text-[#0c98d0]">
                    v{entry.version}
                  </span>

                  <span className="min-w-[140px] shrink-0 text-[13px] text-[#8fa8b2]">
                    {entry.displayDate}
                  </span>

                  <div className="min-w-0">
                    <div className="mb-1 text-[15px] font-semibold text-[#17313a]">
                      {entry.title}
                    </div>
                    <div className="line-clamp-2 text-[14px] leading-[1.5] text-[#55717c]">
                      {entry.description}
                    </div>
                  </div>

                  <span className="whitespace-nowrap text-[13px] text-[#0c98d0]">
                    View
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ))}
    </div>
  );
}

export default AllReleaseNotesPage;
